// Single source of truth for T.A.C.C. pricing — powers the Admin "Pricing & Products"
// reference tab and the printable one-pager. Reference data only; the live checkout
// prices live in the backend PRODUCT_CATALOG and are unchanged by this file.

export const GUMROAD_URL = "https://richculm.gumroad.com/l/lkejj";

// Bought directly on TACC.love (on-site Stripe checkout)
export const TACC_PRODUCTS = [
  {
    key: "workbook",
    name: "Workbook (Audio)",
    price: 37,
    unlocks: "Audio workbook + all 8 modules",
    desc: "The guided audio companion — reflections for every one of the 8 modules.",
  },
  {
    key: "course",
    name: "Video Course",
    price: 57,
    unlocks: "Video course + all 8 modules",
    desc: "All 8 healing module videos, taught start to finish.",
  },
  {
    key: "bundle",
    name: "Complete Bundle",
    price: 77,
    unlocks: "Everything — audio workbook + video course + all 8 modules",
    desc: "The full experience in one purchase — best value.",
    best: true,
  },
];

// Same titles sold on Gumroad (the links you send people to off-site)
export const GUMROAD_PRODUCTS = [
  { key: "workbook", name: "The Workbook", price: 47 },
  { key: "course", name: "The Video Course", price: 67 },
  { key: "bundle", name: "The Complete Bundle", price: 97 },
];

// Books on Amazon (Amazon sets the retail price)
export const AMAZON_BOOKS = [
  {
    name: "T.A.C.C. Work Book — The Guided Companion",
    format: "Paperback / Kindle",
    url: "https://www.amazon.com/dp/B0H5D49156",
    desc: "The printable workbook — deep reflection questions & exercises for all 8 modules.",
  },
  {
    name: "T.A.C.C. Manual — Guide to Healing",
    format: "Kindle",
    url: "https://www.amazon.com/dp/B0G5286848",
    desc: "The foundational doctrine — the core manual to heal, grow and feel whole again.",
  },
];

export const MODULES = [
  { number: "01", title: "Understanding God", duration: "1:32", focus: "The foundation · God is love" },
  { number: "02", title: "Accepting Where You Are", duration: "1:26", focus: "Responsibility & acceptance" },
  { number: "03", title: "Embracing Your Journey", duration: "1:18", focus: "Purpose in your story" },
  { number: "04", title: "The Wall of Adversity", duration: "1:25", focus: "Facing what you hide" },
  { number: "05", title: "The Decision", duration: "1:16", focus: "The turning point" },
  { number: "06", title: "Knowledge — Facing Your Past", duration: "1:13", focus: "The first article" },
  { number: "07", title: "Wisdom — Processing Your Pain", duration: "1:20", focus: "Seeing it clearly" },
  { number: "08", title: "Understanding — Becoming the Healer", duration: "1:19", focus: "Preparation, not pain" },
];

export const B2B_TIERS = [
  {
    key: "starter",
    name: "Starter",
    who: "Small teams · up to 25 people",
    price: 1200,
    unit: "/quarter",
    checkout: true,
    features: ["Access to 4 core modules", "1 Kindle site license", "Slack / Teams onboarding kit", "Quarterly office hours"],
  },
  {
    key: "team",
    name: "Team",
    who: "Departments · 25–250 people",
    price: 4800,
    unit: "/quarter",
    checkout: true,
    featured: true,
    features: ["All 8 healing modules", "Bulk paperback discount (30%)", "Live pre-shift workshop (2/quarter)", "Manager training playbook", "Anonymous impact dashboard"],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    who: "Companies · 250+ people",
    price: null,
    unit: "per-seat / year",
    checkout: false,
    features: ["White-labeled portal for staff", "Custom module authored for your industry", "On-site executive workshops", "Dedicated wellness strategist", "Quarterly board-level report"],
  },
];

// Proposed Enterprise per-seat pricing (annual) — the defaults; the live values are
// stored in the backend (app_settings) and editable from the admin Pricing tab.
export const ENTERPRISE_SEAT_TIERS = [
  { min: 250, max: 999, perSeatYear: 36 },
  { min: 1000, max: 2499, perSeatYear: 28 },
  { min: 2500, max: 4999, perSeatYear: 20 },
  { min: 5000, max: Infinity, perSeatYear: 15 },
];

// Representative headcount for each quote-form size bucket (mirrors backend SIZE_TO_SEATS)
export const SIZE_TO_SEATS = { "1-24": 20, "25-100": 75, "100-500": 300, "500-2500": 1500, "2500+": 2500 };

// Editable reference prices (defaults) — the numbers you quote. The live checkout charge
// is set in Stripe; editing here changes your reference sheet, not what customers are billed.
export const DEFAULT_REFERENCE_PRICES = {
  tacc: { workbook: 37, course: 57, bundle: 77 },
  gumroad: { workbook: 47, course: 67, bundle: 97 },
  b2b: { starter: 1200, team: 4800 },
};

// merge saved overrides onto the defaults, keeping only known keys
export const mergeReferencePrices = (saved) => {
  const out = { tacc: {}, gumroad: {}, b2b: {} };
  for (const group of Object.keys(DEFAULT_REFERENCE_PRICES)) {
    for (const [k, v] of Object.entries(DEFAULT_REFERENCE_PRICES[group])) {
      const s = saved?.[group]?.[k];
      out[group][k] = s == null ? v : Number(s);
    }
  }
  return out;
};

// backend {min,max:null,per_seat_year} -> UI {min,max:Infinity,perSeatYear}
export const tiersFromApi = (apiTiers) =>
  (apiTiers || []).map((t) => ({ min: t.min, max: t.max == null ? Infinity : t.max, perSeatYear: t.per_seat_year }));

// UI -> backend
export const tiersToApi = (tiers) =>
  (tiers || []).map((t) => ({ min: Number(t.min), max: t.max === Infinity ? null : Number(t.max), per_seat_year: Number(t.perSeatYear) }));

export function enterpriseQuote(headcount, tiers = ENTERPRISE_SEAT_TIERS) {
  const n = Number(headcount);
  if (!n || n < 1) return null;
  const tier = (tiers || []).find((t) => n >= t.min && n <= t.max);
  if (!tier) return null;
  return { perSeatYear: tier.perSeatYear, annual: n * tier.perSeatYear, headcount: n };
}

// Turn a lead's size bucket into an enterprise estimate (250+ only)
export function enterpriseQuoteForSize(sizeBucket, tiers = ENTERPRISE_SEAT_TIERS) {
  const seats = SIZE_TO_SEATS[sizeBucket];
  if (!seats || seats < 250) return null;
  const q = enterpriseQuote(seats, tiers);
  return q ? { seats, perSeatYear: q.perSeatYear, annual: q.annual } : null;
}

export const money = (n) =>
  typeof n === "number" ? `$${n.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : n;

const seatTierLabel = (t) =>
  t.max === Infinity ? `${t.min.toLocaleString()}+` : `${t.min.toLocaleString()}–${t.max.toLocaleString()}`;

// Build a clean, light-themed, print-ready one-pager and open it for Save-as-PDF.
export function openPricingSheet(headcount = 2500, tiers = ENTERPRISE_SEAT_TIERS, prices = DEFAULT_REFERENCE_PRICES) {
  const q = enterpriseQuote(headcount, tiers);
  const row = (cells) => `<tr>${cells.map((c) => `<td>${c}</td>`).join("")}</tr>`;
  const pr = prices || DEFAULT_REFERENCE_PRICES;

  const taccRows = TACC_PRODUCTS.map((p) =>
    row([`<strong>${p.name}</strong>`, money(pr.tacc?.[p.key] ?? p.price), p.unlocks])
  ).join("");
  const gumRows = GUMROAD_PRODUCTS.map((p) => row([p.name, money(pr.gumroad?.[p.key] ?? p.price), "Gumroad"])).join("");
  const bookRows = AMAZON_BOOKS.map((b) => row([b.name, b.format, "Amazon-priced"])).join("");
  const b2bRows = B2B_TIERS.map((t) =>
    row([`<strong>${t.name}</strong><br><span class="muted">${t.who}</span>`,
      t.checkout ? `${money(pr.b2b?.[t.key] ?? t.price)}${t.unit}` : "Custom (per-seat/yr)",
      t.features.join(" · ")])
  ).join("");
  const seatRows = (tiers || ENTERPRISE_SEAT_TIERS).map((t) =>
    row([`${seatTierLabel(t)} employees`, `${money(t.perSeatYear)} / seat / year`])
  ).join("");

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>T.A.C.C. — Pricing Sheet</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, Segoe UI, Helvetica, Arial, sans-serif; color: #111; margin: 0; padding: 32px 40px; }
    h1 { font-size: 22px; margin: 0 0 2px; letter-spacing: -0.01em; }
    .sub { color: #B45309; font-size: 11px; letter-spacing: 3px; text-transform: uppercase; margin: 0 0 18px; }
    h2 { font-size: 13px; letter-spacing: 2px; text-transform: uppercase; color: #B45309; margin: 22px 0 8px; border-bottom: 1px solid #eee; padding-bottom: 6px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; margin: 0 0 6px; }
    td { padding: 7px 8px; border-bottom: 1px solid #eee; vertical-align: top; }
    .muted { color: #888; font-size: 11px; }
    .note { background: #FFF7ED; border: 1px solid #FED7AA; padding: 10px 14px; border-radius: 8px; font-size: 12px; color: #7C2D12; margin: 6px 0 0; }
    .big { font-size: 15px; font-weight: 700; color: #B45309; }
    @media print { body { padding: 16px 20px; } @page { margin: 12mm; } }
  </style></head><body onload="window.print()">
    <h1>T.A.C.C. — Pricing &amp; Products</h1>
    <p class="sub">Through Adversity Comes Change · Founder reference sheet</p>

    <h2>On TACC.love (your site — Stripe checkout)</h2>
    <table><tbody>${taccRows}</tbody></table>

    <h2>On Gumroad (off-site links)</h2>
    <table><tbody>${gumRows}</tbody></table>

    <h2>Books (Amazon)</h2>
    <table><tbody>${bookRows}</tbody></table>

    <h2>For companies (B2B)</h2>
    <table><tbody>${b2bRows}</tbody></table>

    <h2>Enterprise — per-seat / year</h2>
    <table><tbody>${seatRows}</tbody></table>
    ${q ? `<p class="note">Example — <strong>${q.headcount.toLocaleString()} employees</strong> at ${money(q.perSeatYear)}/seat/year = <span class="big">${money(q.annual)} / year</span></p>` : ""}
  </body></html>`;

  const w = window.open("", "_blank");
  if (w) {
    w.document.write(html);
    w.document.close();
  }
}
